import { Link } from 'react-router-dom';
import { Instagram, Twitter, Facebook, Mail, Phone } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.87a8.28 8.28 0 004.76 1.5V6.93a4.84 4.84 0 01-1-.24z"/>
  </svg>
);

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer-section mt-20">
      {/* Newsletter Section */}
      <div className="bg-[#FAD4D4] border-b-2 border-[#0A0A0A] py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            JOIN THE DOE WIZ FAMILY
          </h3>
          <p className="text-[#525252] mb-6 max-w-lg mx-auto">
            Subscribe to our newsletter for exclusive deals, new arrivals, and style inspiration.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email"
              className="flex-1 border-2 border-[#0A0A0A] rounded-none h-12 focus:ring-0 focus:bg-white"
              required
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-12 px-8 font-semibold hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <h2 className="font-black text-2xl tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              DOE WIZ
            </h2>
            <p className="text-[#525252] text-sm leading-relaxed mb-4">
              Quality apparel for the whole family. From streetwear to everyday essentials, we've got you covered.
            </p>
            <div className="flex gap-4">
              <a href="https://x.com/DoeWizApparel" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-[#0A0A0A] hover:bg-[#FAD4D4] transition-colors" data-testid="social-twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/doewizapparel" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-[#0A0A0A] hover:bg-[#D4E2FA] transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/doewizfanpage" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-[#0A0A0A] hover:bg-[#D5FDD8] transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@doewiz" target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-[#0A0A0A] hover:bg-[#F0D5FD] transition-colors" data-testid="social-tiktok">
                <TikTokIcon />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4">Shop</h4>
            <ul className="space-y-2">
              <li><Link to="/products?category=men" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Men</Link></li>
              <li><Link to="/products?category=women" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Women</Link></li>
              <li><Link to="/products?category=boys" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Boys</Link></li>
              <li><Link to="/products?category=girls" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Girls</Link></li>
              <li><Link to="/products?category=babies" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Babies</Link></li>
              <li><Link to="/products?category=hats" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Hats</Link></li>
              {/* Accessories link suspended 2026-05 */}
            </ul>
          </div>

          {/* Help */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4">Help</h4>
            <ul className="space-y-2">
              <li><Link to="/size-guide" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Size Guide</Link></li>
              <li><a href="#" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Shipping Info</a></li>
              <li><a href="#" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Returns & Exchanges</a></li>
              <li><a href="#" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">FAQ</a></li>
              <li><a href="#" className="text-[#525252] hover:text-[#0A0A0A] transition-colors">Contact Us</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-[#525252]">
                <Mail className="w-4 h-4" />
                <a href="mailto:DoeWizapparel@gmail.com" className="text-sm hover:text-[#0A0A0A] transition-colors">DoeWizapparel@gmail.com</a>
              </li>
              <li className="flex items-center gap-2 text-[#525252]">
                <Phone className="w-4 h-4" />
                <a href="tel:+13474267813" className="text-sm hover:text-[#0A0A0A] transition-colors">+1 (347) 426-7813</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t-2 border-[#0A0A0A] mt-12 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#525252]">
            © {new Date().getFullYear()} Doe Wiz Apparel. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors">Privacy Policy</a>
            <a href="#" className="text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
